import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mail, Eye, ArrowRight, MessageSquare, PhoneCall } from "lucide-react";
import { EMAIL_TEMPLATES, EmailTemplate, getAllCategories } from "@/data/emailTemplates";
import { SequencePreviewFrame } from "./SequencePreviewFrame";

interface EmailTemplateGalleryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (template: EmailTemplate) => void;
}

export default function EmailTemplateGallery({ 
  open, 
  onOpenChange, 
  onSelectTemplate 
}: EmailTemplateGalleryProps) {
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [previewStepIndex, setPreviewStepIndex] = useState(0);
  const categories = getAllCategories();

  const handleUseTemplate = (template: EmailTemplate) => {
    onSelectTemplate(template);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Template Library
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${categories.length + 1}, 1fr)` }}>
              <TabsTrigger value="all">All Templates</TabsTrigger>
              {categories.map(cat => (
                <TabsTrigger key={cat} value={cat}>{cat}</TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="all">
              <ScrollArea className="h-[calc(90vh-180px)]">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {EMAIL_TEMPLATES.map(template => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      onPreview={(t) => {
                        setPreviewTemplate(t);
                        setPreviewStepIndex(0);
                      }}
                      onUse={handleUseTemplate}
                    />
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            {categories.map(category => (
              <TabsContent key={category} value={category}>
                <ScrollArea className="h-[calc(90vh-180px)]">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                    {EMAIL_TEMPLATES.filter(t => t.category === category).map(template => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onPreview={(t) => {
                          setPreviewTemplate(t);
                          setPreviewStepIndex(0);
                        }}
                        onUse={handleUseTemplate}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <Badge>{previewTemplate?.category}</Badge>
                <p className="text-sm text-muted-foreground mt-2">
                  {previewTemplate?.description}
                </p>
                {previewTemplate && (
                  <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                    <div><span className="font-semibold text-foreground">Best for:</span> {previewTemplate.bestFor}</div>
                    <div className="mt-1"><span className="font-semibold text-foreground">Trigger:</span> {previewTemplate.trigger}</div>
                  </div>
                )}
              </div>
              
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">Sequence Steps ({previewTemplate?.steps.length})</h3>
                {previewTemplate?.steps.map((step, idx) => (
                  <Button
                    key={idx}
                    variant={previewStepIndex === idx ? "default" : "outline"}
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => setPreviewStepIndex(idx)}
                  >
                    {step.type === "sms" ? <MessageSquare className="h-3 w-3 mr-2" /> : step.type === "task" ? <PhoneCall className="h-3 w-3 mr-2" /> : <Mail className="h-3 w-3 mr-2" />}
                    Day {step.delayDays || 0}: {step.subject.substring(0, 30)}...
                  </Button>
                ))}
              </div>

              <Button 
                className="w-full" 
                onClick={() => previewTemplate && handleUseTemplate(previewTemplate)}
              >
                Use This Template <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted p-3 border-b">
                <p className="text-sm font-medium">Preview: Step {previewStepIndex + 1}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {previewTemplate?.steps[previewStepIndex]?.subject}
                </p>
              </div>
              <ScrollArea className="h-[calc(90vh-280px)]">
                <SequencePreviewFrame
                  className="m-4"
                  type={previewTemplate?.steps[previewStepIndex]?.type || "email"}
                  subject={previewTemplate?.steps[previewStepIndex]?.subject}
                  html={previewTemplate?.steps[previewStepIndex]?.body || ""}
                />
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TemplateCard({ 
  template, 
  onPreview, 
  onUse 
}: { 
  template: EmailTemplate;
  onPreview: (template: EmailTemplate) => void;
  onUse: (template: EmailTemplate) => void;
}) {
  return (
    <Card className="hover:border-primary transition-colors">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-base">{template.name}</CardTitle>
            <Badge variant="outline" className="mt-2">{template.category}</Badge>
          </div>
          <Mail className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="line-clamp-2">{template.description}</CardDescription>
        <div className="mt-3 space-y-1 text-xs text-muted-foreground">
          <p>{template.steps.length} touches • {template.trigger}</p>
          <p className="line-clamp-2">{template.bestFor}</p>
        </div>
      </CardContent>
      <CardFooter className="gap-2">
        <Button variant="outline" size="sm" onClick={() => onPreview(template)} className="flex-1">
          <Eye className="h-3 w-3 mr-1" />
          Preview
        </Button>
        <Button size="sm" onClick={() => onUse(template)} className="flex-1">
          Use Template
        </Button>
      </CardFooter>
    </Card>
  );
}